import {
  DOM_IDS,
  LAYOUT_CONFIG,
} from "@circle-react-uikit/CircleAIAppShell/Layout/defaults";
import { ConversationActionsProvider } from "@circle-react/components/CircleAi/Home/ConversationActions/ConversationActionsProvider";
import { ConversationActionsTrigger } from "@circle-react/components/CircleAi/Home/ConversationActions/ConversationActionsTrigger";
import { LoaderRow } from "@circle-react/components/shared/LoaderRow";
import { formattedMessageSentAt } from "@circle-react/helpers/dateTimeHelpers/formattedMessageSentAt";
import { useDebouncedValue } from "@circle-react/hooks/utils/useDebouncedValue";
import { IconButton } from "@circleco/compass/components/IconButton";
import { SearchInput } from "@circleco/compass/components/SearchInput";
import { SideDrawer } from "@circleco/compass/components/SideDrawer";
import { Typography } from "@circleco/compass/components/Typography";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useState, type ChangeEvent } from "react";
import { createPortal } from "react-dom";
import InfiniteScroll from "react-infinite-scroll-component";

import { t } from "@/i18n-js/instance";

import { ConversationCard } from "../Home/ConversationCard";
import { useRecentChatsList } from "../Home/RecentChatsDrawer/useRecentChatsList";
import { useCircleAiStore } from "../useCircleAiStore";
import { useOpenRecentChat } from "../useOpenRecentChat";

const SEARCH_DEBOUNCE_MS = 250;
const SCROLLABLE_TARGET_ID = "circle-ai-recent-chats-flyover-scroll";

/**
 * The recent-chats slider, shared by the expanded dock ("More" button) and the
 * collapsed dock (clock icon) via `isDockRecentChatsOpen` in the store, so both
 * dock modes open the same overlay. Rendered once at the dock level; the portal
 * container is mounted into the shell body (below the banners/top chrome) and
 * positioned right of the dock (any width) via `--shell-dock-w`, so the drawer
 * stays scoped within the shell instead of overlapping the top chrome.
 */
export const CircleAIDockRecentChatsFlyover = () => {
  const pathname = useLocation({ select: location => location.pathname });
  const [searchValue, setSearchValue] = useState("");
  const [drawerContainer, setDrawerContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const [shellBody, setShellBody] = useState<HTMLElement | null>(null);
  const debouncedQuery = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS);
  const isOpen = useCircleAiStore(s => s.isDockRecentChatsOpen);
  const setIsOpen = useCircleAiStore(s => s.setDockRecentChatsOpen);
  const openRecentChat = useOpenRecentChat();

  const { data, fetchNextPage, hasNextPage, isPending, isError } =
    useRecentChatsList({ query: debouncedQuery, enabled: isOpen });
  const conversations = data?.pages.flatMap(page => page.records) ?? [];
  const hasConversations = conversations.length > 0;
  const isInitialLoading = isPending && !hasConversations;
  const isEmpty = !isPending && !isError && !hasConversations;

  useEffect(() => {
    setIsOpen(false);
  }, [pathname, setIsOpen]);

  // Anchor the overlay to the shell body (below the banners/top chrome) rather
  // than the viewport, so the drawer stays within the shell instead of escaping
  // to the very top.
  useLayoutEffect(() => {
    setShellBody(document.getElementById(DOM_IDS.shellBody));
  }, []);

  return (
    <>
      <ConversationActionsProvider>
        <SideDrawer.Root
          open={isOpen}
          onOpenChange={setIsOpen}
          position="left"
          container={drawerContainer}
        >
          <SideDrawer.Content
            aria-label={t("circle_ai_home.recent_chats_drawer.title")}
          >
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex h-16 shrink-0 items-center gap-2 border-b border-primary px-5 py-3">
                <Typography
                  variant="heading-sm"
                  color="primary"
                  component="h2"
                  className="flex-1 truncate"
                >
                  {t("circle_ai_home.recent_chats_drawer.title")}
                </Typography>
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon="cross"
                  aria-label={t("close")}
                  onClick={() => setIsOpen(false)}
                />
              </div>
              <div className="shrink-0 p-4">
                <SearchInput
                  placeholder={t(
                    "circle_ai_home.recent_chats_drawer.search_placeholder",
                  )}
                  value={searchValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSearchValue(e.target.value)
                  }
                  onClear={() => setSearchValue("")}
                />
              </div>
              <div
                id={SCROLLABLE_TARGET_ID}
                className="flex-1 overflow-y-auto px-4 pb-4"
              >
                {isInitialLoading && <LoaderRow />}
                {isError && (
                  <Typography variant="body-sm" color="tertiary" component="p">
                    {t("circle_ai_home.recent_chats_drawer.load_error")}
                  </Typography>
                )}
                {isEmpty && (
                  <Typography variant="body-sm" color="tertiary" component="p">
                    {t(
                      debouncedQuery.length > 0
                        ? "circle_ai_home.recent_chats_drawer.empty_search"
                        : "circle_ai_home.recent_chats_drawer.empty",
                    )}
                  </Typography>
                )}
                {hasConversations && (
                  <InfiniteScroll
                    style={{ overflow: "initial" }}
                    scrollThreshold={0.8}
                    next={fetchNextPage}
                    hasMore={Boolean(hasNextPage)}
                    loader={<LoaderRow />}
                    scrollableTarget={SCROLLABLE_TARGET_ID}
                    dataLength={conversations.length}
                  >
                    <div className="flex flex-col gap-4">
                      {conversations.map(conversation => (
                        <ConversationCard
                          key={conversation.uuid}
                          title={
                            conversation.title ??
                            t("circle_ai_home.new_conversation")
                          }
                          preview={conversation.last_message_preview}
                          dateLabel={
                            formattedMessageSentAt(conversation.updated_at).day
                          }
                          onClick={() =>
                            openRecentChat(
                              conversation.uuid,
                              conversation.title,
                              conversation.project_id,
                            )
                          }
                          actions={
                            <ConversationActionsTrigger
                              conversation={{
                                uuid: conversation.uuid,
                                title: conversation.title,
                                projectUuid: conversation.project_id,
                                isDailyBriefConversation:
                                  conversation.daily_brief_conversation,
                              }}
                            />
                          }
                        />
                      ))}
                    </div>
                  </InfiniteScroll>
                )}
              </div>
            </div>
          </SideDrawer.Content>
        </SideDrawer.Root>
      </ConversationActionsProvider>
      {shellBody &&
        createPortal(
          <div
            ref={setDrawerContainer}
            style={{ left: `var(${LAYOUT_CONFIG.dock.cssVar}, 0px)` }}
            className={`${isOpen ? "pointer-events-auto" : "pointer-events-none"} absolute inset-y-0 right-0 z-30 overflow-hidden [--color-background-overlay:transparent]`}
          />,
          shellBody,
        )}
    </>
  );
};
